import { twMerge } from '@shared/themes';
import { useFormatter, useTranslations } from 'next-intl';
import type { LotteryName } from '../../lottery-purchases/types';
import { interpolateMarkup } from './interpolateMarkup';
import { LotteryFaqAccordion } from './LotteryFaqAccordion';
import { LotteryResultsFaqItems } from './LotteryResultsFaqItems';

type FaqItem = {
  question: string;
  answer: string;
};

type FaqPage = {
  lotteryName: LotteryName;
  faqPage?: FaqType;
  className?: string;
  closingDate?: number;
  drawingDate?: number;
};

type FaqType = 'lottery' | 'results';

const TIME_FORMAT = { hour: 'numeric', minute: '2-digit', hourCycle: 'h12' } as const;

/**
 * LotteryFAQ section with id lottery-faq for document selection to enable scrollIntoView
 * @param lotteryName name of the lottery as per translation key in lotteries.json
 * @returns lottery specific FAQ section derived from lotteries.json, using the FaqSection shared component
 */
function LotteryFAQ({ lotteryName, faqPage = 'lottery', className, closingDate, drawingDate }: FaqPage) {
  const t = useTranslations(`lotteries.${lotteryName}.${faqPage}.faqs`);
  const format = useFormatter();
  const faqItems = t.has('faq_items') ? (t.raw('faq_items') as FaqItem[]) : [];
  const hasExplicitDates = closingDate !== undefined && drawingDate !== undefined;
  const dateValues = hasExplicitDates
    ? {
        closingDate: format.dateTime(new Date(closingDate), TIME_FORMAT),
        drawingDate: format.dateTime(new Date(drawingDate), TIME_FORMAT),
      }
    : null;

  return (
    <div id="lottery-faq" className={twMerge('flex flex-col', className, 'gap-4')} data-track-section="FAQs" data-testid="lottery-faq">
      <h2 className="heading-5 text-foreground">{t('title')}</h2>
      {faqItems.length === 0 ? null : faqPage === 'results' && !hasExplicitDates ? (
        <LotteryResultsFaqItems lotteryName={lotteryName} items={faqItems} />
      ) : (
        <LotteryFaqAccordion items={faqItems.map(item => ({ ...item, answer: interpolateMarkup(item.answer, dateValues ?? {}) }))} />
      )}
    </div>
  );
}

export { LotteryFAQ };
